<template>
  <div class="p-6">
    <div class="mb-6">
      <h1 class="text-2xl font-bold text-gray-900 dark:text-white mb-2">
        Gestión Completa de Catálogos
      </h1>
      <p class="text-gray-600 dark:text-gray-400">
        Administra catálogos y sus campos con configuración de fórmulas de cálculo
      </p>
    </div>

    <div class="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700">
      <div class="p-6 border-b border-gray-200 dark:border-gray-700">
        <div class="flex justify-between items-center">
          <h2 class="text-lg font-semibold text-gray-900 dark:text-white">
            Lista de Catálogos
          </h2>
          <button
            @click="openCatalogoModal()"
            class="bg-violet-600 hover:bg-violet-700 text-white px-4 py-2 rounded-lg transition-colors duration-200 flex items-center gap-2"
          >
            <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 4v16m8-8H4"/>
            </svg>
            Nuevo Catálogo
          </button>
        </div>
      </div>

      <div class="p-6">
        <div v-if="loading" class="text-center py-8">
          <div class="animate-spin rounded-full h-12 w-12 border-b-2 border-violet-600 mx-auto"></div>
        </div>

        <div v-else class="overflow-x-auto">
          <table class="w-full text-sm text-left text-gray-500 dark:text-gray-400">
            <thead class="text-xs uppercase bg-gray-100 dark:bg-gray-800 dark:text-gray-300 border-b-2 border-gray-200 dark:border-gray-700">
              <tr>
                <th class="px-4 py-2 font-bold">Nombre</th>
                <th class="px-4 py-2 font-bold">Descripción</th>
                <th class="px-4 py-2 font-bold">Campos</th>
                <th class="px-4 py-2 font-bold">Registros</th>
                <th class="px-4 py-2 font-bold">Acciones</th>
              </tr>
            </thead>
            <tbody>
              <tr
                v-for="catalogo in catalogos"
                :key="catalogo.catalogoId"
                class="bg-white border-b dark:bg-gray-800 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-700"
              >
                <td class="px-4 py-3 font-medium text-gray-900 dark:text-white">
                  {{ catalogo.nombreCatalogo }}
                </td>
                <td class="px-4 py-3 text-gray-700 dark:text-gray-200">
                  {{ catalogo.descripcion || '-' }}
                </td>
                <td class="px-4 py-3">
                  <button
                    @click="gestionarCampos(catalogo)"
                    class="text-blue-600 hover:text-blue-800 dark:text-blue-400 dark:hover:text-blue-300 flex items-center gap-1"
                  >
                    <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2"/>
                    </svg>
                    Ver Campos ({{ catalogo.campos?.length || 0 }})
                  </button>
                </td>
                <td class="px-4 py-3">
                  <span class="px-2 py-1 text-xs font-medium rounded-full bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-300">
                    {{ catalogo.registros?.length || 0 }}
                  </span>
                </td>
                <td class="px-4 py-3">
                  <div class="flex gap-2">
                    <button @click="openCatalogoModal(catalogo)" class="text-green-600 hover:text-green-800 dark:text-green-400 dark:hover:text-green-300" title="Editar">
                      <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"/>
                      </svg>
                    </button>
                    <button @click="eliminarCatalogo(catalogo)" class="text-red-600 hover:text-red-800 dark:text-red-400 dark:hover:text-red-300" title="Eliminar">
                      <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"/>
                      </svg>
                    </button>
                  </div>
                </td>
              </tr>
              <tr v-if="catalogos.length === 0">
                <td colspan="5" class="px-4 py-8 text-center text-gray-500 dark:text-gray-400">
                  No hay catálogos registrados
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </div>

    <!-- Modal: Crear/Editar Catálogo -->
    <div v-if="showCatalogoModal" class="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div class="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-lg w-full">
        <div class="p-6 border-b border-gray-200 dark:border-gray-700">
          <h3 class="text-lg font-semibold text-gray-900 dark:text-white">
            {{ catalogoEditando ? 'Editar Catálogo' : 'Nuevo Catálogo' }}
          </h3>
        </div>

        <div class="p-6 space-y-4">
          <div>
            <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Nombre del Catálogo *
            </label>
            <input
              v-model="catalogoForm.nombreCatalogo"
              type="text"
              class="w-full px-3 py-2 rounded border bg-gray-50 dark:bg-gray-900 dark:border-gray-700 focus:outline-none focus:ring-2 focus:ring-violet-200"
              placeholder="Ej: EMPAQUE ESPECIAL"
            />
          </div>

          <div>
            <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Descripción
            </label>
            <textarea
              v-model="catalogoForm.descripcion"
              rows="3"
              class="w-full px-3 py-2 rounded border bg-gray-50 dark:bg-gray-900 dark:border-gray-700 focus:outline-none focus:ring-2 focus:ring-violet-200"
              placeholder="Descripción del catálogo"
            ></textarea>
          </div>
        </div>

        <div class="p-6 border-t border-gray-200 dark:border-gray-700 flex justify-end gap-2">
          <button
            @click="closeCatalogoModal"
            class="px-4 py-2 bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-200 rounded-lg hover:bg-gray-300 dark:hover:bg-gray-600 transition"
          >
            Cancelar
          </button>
          <button
            @click="guardarCatalogo"
            :disabled="loading"
            class="px-4 py-2 bg-violet-600 hover:bg-violet-700 text-white rounded-lg transition disabled:opacity-50"
          >
            {{ loading ? 'Guardando...' : 'Guardar' }}
          </button>
        </div>
      </div>
    </div>

    <!-- Modal: Gestionar Campos del Catálogo -->
    <div v-if="showCamposModal" class="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50 overflow-y-auto">
      <div class="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-4xl w-full my-8">
        <div class="p-6 border-b border-gray-200 dark:border-gray-700">
          <div class="flex justify-between items-center">
            <div>
              <h3 class="text-lg font-semibold text-gray-900 dark:text-white">
                Campos de: {{ catalogoSeleccionado?.nombreCatalogo }}
              </h3>
              <p class="text-sm text-gray-500 dark:text-gray-400 mt-1">
                Configura los campos y sus fórmulas de cálculo
              </p>
            </div>
            <button
              @click="closeCamposModal"
              class="text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
            >
              <svg class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"/>
              </svg>
            </button>
          </div>
        </div>

        <div class="p-6">
          <!-- Lista de campos existentes -->
          <div class="mb-6">
            <h4 class="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-3">Campos Configurados</h4>
            <div v-if="camposCatalogo.length === 0" class="text-center py-8 text-gray-500 dark:text-gray-400 bg-gray-50 dark:bg-gray-900 rounded-lg">
              No hay campos configurados. Agrega el primer campo.
            </div>
            <div v-else class="space-y-2">
              <div
                v-for="(campo, index) in camposCatalogo"
                :key="campo.campoId || index"
                class="flex items-center gap-3 p-3 border border-gray-200 dark:border-gray-700 rounded-lg bg-gray-50 dark:bg-gray-900"
              >
                <div class="flex-1">
                  <div class="font-medium text-gray-900 dark:text-white">{{ campo.nombreCampo }}</div>
                  <div class="text-xs text-gray-500 dark:text-gray-400 mt-1 space-x-2">
                    <span v-if="campo.esMonto" class="px-2 py-0.5 bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400 rounded">
                      💰 {{ getTipoMontoLabel(campo.tipoMonto) }}
                    </span>
                    <span v-if="campo.esCalculado" class="px-2 py-0.5 bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-400 rounded">
                      🔢 {{ getTipoCalculoLabel(campo.tipoCalculo) }}
                    </span>
                    <span v-if="campo.esCatalogo" class="px-2 py-0.5 bg-violet-100 dark:bg-violet-900/30 text-violet-700 dark:text-violet-400 rounded">
                      📋 Catálogo: {{ getCatalogoReferenciaLabel(campo.catalogoReferencia) }}
                    </span>
                    <span v-if="!campo.esMonto && !campo.esCalculado && !campo.esCatalogo" class="px-2 py-0.5 bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400 rounded">📝 Texto/Número</span>
                  </div>
                </div>
                <div class="flex gap-2">
                  <button
                    @click="editarCampo(campo, index)"
                    class="text-blue-600 hover:text-blue-800 dark:text-blue-400 dark:hover:text-blue-300"
                    title="Editar"
                  >
                    <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"/>
                    </svg>
                  </button>
                  <button
                    @click="eliminarCampo(campo, index)"
                    class="text-red-600 hover:text-red-800 dark:text-red-400 dark:hover:text-red-300"
                    title="Eliminar"
                  >
                    <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"/>
                    </svg>
                  </button>
                </div>
              </div>
            </div>
          </div>

          <!-- Formulario para agregar/editar campo -->
          <div class="border-t border-gray-200 dark:border-gray-700 pt-6">
            <h4 class="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-4">
              {{ campoEditandoIndex !== null ? 'Editar Campo' : 'Agregar Nuevo Campo' }}
            </h4>
            <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Nombre del Campo *
                </label>
                <input
                  v-model="campoForm.nombreCampo"
                  type="text"
                  class="w-full px-3 py-2 rounded border bg-gray-50 dark:bg-gray-900 dark:border-gray-700 focus:outline-none focus:ring-2 focus:ring-violet-200"
                  placeholder="Ej: Costo, Precio, Cantidad"
                />
              </div>

              <div>
                <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Orden
                </label>
                <input
                  v-model.number="campoForm.orden"
                  type="number"
                  class="w-full px-3 py-2 rounded border bg-gray-50 dark:bg-gray-900 dark:border-gray-700 focus:outline-none focus:ring-2 focus:ring-violet-200"
                  placeholder="1"
                />
              </div>

              <div class="flex items-center gap-4 flex-wrap">
                <label class="flex items-center gap-2 cursor-pointer">
                  <input
                    v-model="campoForm.esMonto"
                    @change="campoForm.esMonto && (campoForm.esCalculado = false, campoForm.esCatalogo = false)"
                    type="checkbox"
                    class="w-4 h-4 text-violet-600 bg-gray-100 border-gray-300 rounded focus:ring-violet-500"
                  />
                  <span class="text-sm font-medium text-gray-700 dark:text-gray-300">💰 Es Monto</span>
                </label>

                <label class="flex items-center gap-2 cursor-pointer">
                  <input
                    v-model="campoForm.esCalculado"
                    @change="campoForm.esCalculado && (campoForm.esMonto = false, campoForm.esCatalogo = false)"
                    type="checkbox"
                    class="w-4 h-4 text-violet-600 bg-gray-100 border-gray-300 rounded focus:ring-violet-500"
                  />
                  <span class="text-sm font-medium text-gray-700 dark:text-gray-300">🔢 Es Calculado</span>
                </label>

                <label class="flex items-center gap-2 cursor-pointer">
                  <input
                    v-model="campoForm.esCatalogo"
                    @change="campoForm.esCatalogo && (campoForm.esMonto = false, campoForm.esCalculado = false)"
                    type="checkbox"
                    class="w-4 h-4 text-violet-600 bg-gray-100 border-gray-300 rounded focus:ring-violet-500"
                  />
                  <span class="text-sm font-medium text-gray-700 dark:text-gray-300">📋 Es Catálogo</span>
                </label>
              </div>

              <div v-if="campoForm.esMonto">
                <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Tipo de Monto *
                </label>
                <select
                  v-model="campoForm.tipoMonto"
                  class="w-full px-3 py-2 rounded border bg-gray-50 dark:bg-gray-900 dark:border-gray-700 focus:outline-none focus:ring-2 focus:ring-violet-200"
                >
                  <option value="">Seleccionar tipo...</option>
                  <option value="afectado_por_poses">Costo Afectado por poses</option>
                  <option value="costo_adicional">Costo Adicional</option>
                  <option value="costo_ajuste">Costo de Ajuste</option>
                  <option value="herramental">Costo Herramental</option>
                  <option value="referencia">Solo Referencia (no suma al costo)</option>
                </select>
                <p v-if="campoForm.tipoMonto === 'referencia'" class="text-xs text-gray-500 dark:text-gray-400 mt-1">
                  💡 Este campo podrá ser usado por otros campos en sus fórmulas, pero no se sumará directamente al costo del prototipo
                </p>
              </div>

              <div v-if="campoForm.esCalculado">
                <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Tipo de Cálculo *
                </label>
                <select
                  v-model="campoForm.tipoCalculo"
                  class="w-full px-3 py-2 rounded border bg-gray-50 dark:bg-gray-900 dark:border-gray-700 focus:outline-none focus:ring-2 focus:ring-violet-200"
                >
                  <option value="valor_directo">Directo (sin cálculo)</option>
                  <option value="area_papel">Por área de papel</option>
                  <option value="area_corrugado">Por área de corrugado</option>
                  <option value="division_cantidad_paquete">División por Cantidad/Paquete</option>
                  <option value="division_cantidad_tarima">División por Tarima</option>
                  <option value="division_envio">División por Envío</option>
                </select>
              </div>

              <!-- Selector catálogo referenciado -->
              <div v-if="campoForm.esCatalogo" class="md:col-span-2">
                <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Catálogo Referenciado *
                </label>
                <select
                  v-model="campoForm.catalogoReferencia"
                  class="w-full px-3 py-2 rounded border bg-gray-50 dark:bg-gray-900 dark:border-gray-700 focus:outline-none focus:ring-2 focus:ring-violet-200"
                >
                  <option value="">Seleccionar catálogo...</option>
                  <option value="proveedores">Proveedores</option>
                </select>
              </div>

              <!-- Descripción de la fórmula -->
              <div v-if="campoForm.esCalculado && campoForm.tipoCalculo" class="md:col-span-2">
                <div class="p-3 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded text-xs">
                  <span class="font-semibold text-blue-700 dark:text-blue-300">📐 Fórmula: </span>
                  <span class="text-blue-600 dark:text-blue-400">{{ getTipoCalculoFormula(campoForm.tipoCalculo) }}</span>
                </div>
              </div>
            </div>

            <div class="flex justify-end gap-2 mt-4">
              <button
                v-if="campoEditandoIndex !== null"
                @click="cancelarEditarCampo"
                class="px-4 py-2 bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-200 rounded-lg hover:bg-gray-300 dark:hover:bg-gray-600 transition"
              >
                Cancelar
              </button>
              <button
                @click="guardarCampo"
                :disabled="!campoForm.nombreCampo"
                class="px-4 py-2 bg-violet-600 hover:bg-violet-700 text-white rounded-lg transition disabled:opacity-50"
              >
                {{ campoEditandoIndex !== null ? 'Actualizar Campo' : 'Agregar Campo' }}
              </button>
            </div>
          </div>
        </div>

        <div class="p-6 border-t border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900">
          <button
            @click="guardarTodosCampos"
            :disabled="loading"
            class="w-full px-4 py-3 bg-green-600 hover:bg-green-700 text-white rounded-lg transition disabled:opacity-50 font-medium"
          >
            {{ loading ? 'Guardando...' : '💾 Guardar Todos los Cambios' }}
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted } from 'vue'
import { useCatalogosNew } from '../../composables/useCatalogosNew'

const {
  loading,
  catalogos,
  fetchCatalogos,
  createCatalogo,
  updateCatalogo,
  deleteCatalogo,
  updateCamposCatalogo
} = useCatalogosNew()

// Estados para modales
const showCatalogoModal = ref(false)
const showCamposModal = ref(false)
const catalogoEditando = ref(null)
const catalogoSeleccionado = ref(null)

// Formularios
const catalogoForm = ref({
  nombreCatalogo: '',
  descripcion: ''
})

const camposCatalogo = ref([])
const campoEditandoIndex = ref(null)
const campoForm = ref({
  nombreCampo: '',
  esMonto: false,
  tipoMonto: '',
  esCalculado: false,
  tipoCalculo: 'valor_directo',
  esCatalogo: false,
  catalogoReferencia: '',
  orden: 1
})

onMounted(async () => {
  await fetchCatalogos()
})

// ========== Gestión de Catálogos ==========
const openCatalogoModal = (catalogo = null) => {
  if (catalogo) {
    catalogoEditando.value = catalogo
    catalogoForm.value = {
      nombreCatalogo: catalogo.nombreCatalogo,
      descripcion: catalogo.descripcion || ''
    }
  } else {
    catalogoEditando.value = null
    catalogoForm.value = {
      nombreCatalogo: '',
      descripcion: ''
    }
  }
  showCatalogoModal.value = true
}

const closeCatalogoModal = () => {
  showCatalogoModal.value = false
  catalogoEditando.value = null
}

const guardarCatalogo = async () => {
  try {
    if (catalogoEditando.value) {
      await updateCatalogo(catalogoEditando.value.catalogoId, catalogoForm.value)
    } else {
      await createCatalogo(catalogoForm.value)
    }
    closeCatalogoModal()
  } catch (error) {
    console.error('Error al guardar catálogo:', error)
    alert('Error al guardar el catálogo')
  }
}

const eliminarCatalogo = async (catalogo) => {
  if (confirm(`¿Está seguro de eliminar el catálogo "${catalogo.nombreCatalogo}"?`)) {
    try {
      await deleteCatalogo(catalogo.catalogoId)
    } catch (error) {
      console.error('Error al eliminar catálogo:', error)
      alert('Error al eliminar el catálogo')
    }
  }
}

// ========== Gestión de Campos ==========
const gestionarCampos = (catalogo) => {
  catalogoSeleccionado.value = catalogo
  camposCatalogo.value = catalogo.campos ? [...catalogo.campos] : []
  resetCampoForm()
  showCamposModal.value = true
}

const closeCamposModal = () => {
  showCamposModal.value = false
  catalogoSeleccionado.value = null
  camposCatalogo.value = []
  resetCampoForm()
}

const resetCampoForm = () => {
  campoForm.value = {
    nombreCampo: '',
    esMonto: false,
    tipoMonto: '',
    esCalculado: false,
    tipoCalculo: 'valor_directo',
    esCatalogo: false,
    catalogoReferencia: '',
    orden: camposCatalogo.value.length + 1
  }
  campoEditandoIndex.value = null
}

const guardarCampo = () => {
  if (!campoForm.value.nombreCampo) {
    alert('El nombre del campo es obligatorio')
    return
  }

  const campo = {
    ...campoForm.value,
    catalogoId: catalogoSeleccionado.value.catalogoId
  }

  if (campoEditandoIndex.value !== null) {
    // Editar campo existente
    camposCatalogo.value[campoEditandoIndex.value] = campo
  } else {
    // Agregar nuevo campo
    camposCatalogo.value.push(campo)
  }

  resetCampoForm()
}

const editarCampo = (campo, index) => {
  campoForm.value = { ...campo }
  campoEditandoIndex.value = index
}

const cancelarEditarCampo = () => {
  resetCampoForm()
}

const eliminarCampo = (campo, index) => {
  if (confirm(`¿Eliminar el campo "${campo.nombreCampo}"?`)) {
    camposCatalogo.value.splice(index, 1)
  }
}

const guardarTodosCampos = async () => {
  try {
    await updateCamposCatalogo(catalogoSeleccionado.value.catalogoId, camposCatalogo.value)
    closeCamposModal()
  } catch (error) {
    console.error('Error al guardar campos:', error)
    alert('Error al guardar los campos')
  }
}

// ========== Helpers ==========
const getTipoMontoLabel = (tipoMonto) => {
  const labels = {
    'afectado_por_poses': 'Afectado por poses',
    'costo_adicional': 'Costo Adicional',
    'costo_ajuste': 'Costo de Ajuste',
    'herramental': 'Herramental',
    'referencia': 'Solo Referencia'
  }
  return labels[tipoMonto] || 'Monto'
}

const getTipoCalculoLabel = (tipoCalculo) => {
  const labels = {
    'valor_directo': 'Directo',
    'area_papel': 'Por área de papel',
    'area_corrugado': 'Por área de corrugado',
    'division_cantidad_paquete': 'División por cant/paquete',
    'division_cantidad_tarima': 'División por tarima',
    'division_envio': 'División por envío'
  }
  return labels[tipoCalculo] || tipoCalculo
}

const getCatalogoReferenciaLabel = (ref) => {
  const labels = { proveedores: 'Proveedores' }
  return labels[ref] || ref || '—'
}

const getTipoCalculoFormula = (tipoCalculo) => {
  const formulas = {
    'valor_directo': 'Costo base (sin cálculo)',
    'area_papel': 'Costo × Área Papel (m²)',
    'area_corrugado': 'Costo × Área Corrugado (m²)',
    'division_cantidad_paquete': 'Costo ÷ Cantidad por Paquete',
    'division_cantidad_tarima': 'Costo ÷ (Cantidad por Paquete × Paquetes por Tarima)',
    'division_envio': 'Costo ÷ (Cantidad por Paquete × Paquetes por Tarima × Tar x Unidad)'
  }
  return formulas[tipoCalculo] || 'Fórmula no definida'
}
</script>
